import React, { useEffect, useMemo, useState } from "react";
import { api, useAuth, useLang, formatApiError } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, Select, Section, EmptyState } from "../components/primitives";
import { useConfirm } from "../components/ConfirmDialog";
import { Plus, Trash, Copy, X, UserCheck, UserMinus, ArrowsClockwise, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "user", de: "Anwender", en: "User" },
  { value: "handworker", de: "Handwerker", en: "Handworker" },
  { value: "expert", de: "Experte", en: "Expert" },
];

const PERM_KEYS = ["positions", "quotes", "categories", "customers", "invoices", "aufmasse", "settings"];

const PERM_LABELS = {
  de: {
    positions: "Positionen",
    quotes: "Angebote",
    categories: "Kategorien",
    customers: "Kunden",
    invoices: "Rechnungen",
    aufmasse: "Aufmaße",
    settings: "Einstellungen",
  },
  en: {
    positions: "Positions",
    quotes: "Quotes",
    categories: "Categories",
    customers: "Customers",
    invoices: "Invoices",
    aufmasse: "Measurements",
    settings: "Settings",
  },
};

const LEVEL_LABELS = {
  de: {
    full: "Volle Rechte",
    view: "Nur einsehen",
    view_no_price: "Einsehen ohne Preise",
    none: "Kein Zugriff",
    edit_no_delete: "Bearbeiten, kein Löschen",
    view_notes_only: "Nur Notizen bearbeiten",
    view_download: "Einsehen + Download",
  },
  en: {
    full: "Full access",
    view: "View only",
    view_no_price: "View without prices",
    none: "No access",
    edit_no_delete: "Edit, no delete",
    view_notes_only: "Notes only",
    view_download: "View + download",
  },
};

const ROLE_BADGE = {
  admin: { de: "Admin", en: "Admin", cls: "bg-[var(--brand-soft)] text-[var(--brand)]" },
  expert: { de: "Experte", en: "Expert", cls: "bg-[#10B981]/10 text-[#047857]" },
  user: { de: "Anwender", en: "User", cls: "bg-[var(--row-hover)] text-[var(--muted)]" },
  handworker: { de: "Handwerker", en: "Handworker", cls: "bg-[#F59E0B]/15 text-[#B45309]" },
};

function PermissionsEditor({ role, permissions, levels, onChange, lang }) {
  if (role === "admin" || role === "expert") {
    return (
      <div className="text-sm text-[var(--muted)] py-2">
        {lang === "de"
          ? "Diese Rolle hat automatisch volle Rechte auf allen Reitern."
          : "This role automatically has full rights on all tabs."}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {PERM_KEYS.map((k) => {
        const opts = levels?.[k] || ["full", "none"];
        return (
          <Field key={k} label={PERM_LABELS[lang][k]}>
            <Select
              data-testid={`perm-${k}`}
              value={permissions[k] || "none"}
              onChange={(e) => onChange({ ...permissions, [k]: e.target.value })}
            >
              {opts.map((o) => (
                <option key={o} value={o}>{LEVEL_LABELS[lang][o] || o}</option>
              ))}
            </Select>
          </Field>
        );
      })}
    </div>
  );
}

function InviteModal({ open, onClose, onCreated, schema, currentUserRole, lang }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [permissions, setPermissions] = useState(schema?.defaults?.user || {});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPermissions(schema?.defaults?.[role] || {});
  }, [role, schema]);

  useEffect(() => {
    if (open) {
      setEmail(""); setName(""); setRole("user");
      setPermissions(schema?.defaults?.user || {});
      setError("");
    }
  }, [open, schema]);

  if (!open) return null;

  const allowedRoles = ROLE_OPTIONS.filter((r) => r.value !== "expert" || currentUserRole === "admin");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/users/invite", { email, name, role, permissions });
      onCreated(data);
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
            <div>
              <div className="label-overline">Invite</div>
              <h2 className="font-heading text-xl font-semibold">{lang === "de" ? "Nutzer einladen" : "Invite user"}</h2>
            </div>
            <button type="button" onClick={onClose} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid="invite-close">
              <X size={20} />
            </button>
          </div>
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={lang === "de" ? "E-Mail" : "Email"}>
                <TextInput data-testid="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
              </Field>
              <Field label={lang === "de" ? "Name (optional)" : "Name (optional)"}>
                <TextInput data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label={lang === "de" ? "Rolle" : "Role"} className="md:col-span-2">
                <Select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
                  {allowedRoles.map((r) => <option key={r.value} value={r.value}>{r[lang]}</option>)}
                </Select>
              </Field>
            </div>
            <div className="border-t border-[var(--border)] pt-5">
              <div className="label-overline mb-3">{lang === "de" ? "Berechtigungen" : "Permissions"}</div>
              <PermissionsEditor
                role={role}
                permissions={permissions}
                levels={schema?.levels}
                onChange={setPermissions}
                lang={lang}
              />
            </div>
            {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 rounded-sm">{error}</div>}
          </div>
          <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={onClose} data-testid="invite-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Btn>
            <Btn type="submit" disabled={busy} data-testid="invite-submit"><Plus size={16} /> {busy ? "…" : (lang === "de" ? "Einladung erstellen" : "Create invite")}</Btn>
          </div>
        </form>
      </div>
    </div>
  );
}

function InviteLinkBanner({ invite, onDismiss, lang }) {
  const [copied, setCopied] = useState(false);
  if (!invite) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      setCopied(true);
      toast.success(lang === "de" ? "Link kopiert" : "Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select text
    }
  };
  return (
    <div className="swiss-card p-5 mb-6 border-l-4 border-l-[var(--brand)]" data-testid="invite-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="label-overline mb-1">{lang === "de" ? "Einladung erstellt" : "Invite created"}</div>
          <div className="font-medium text-[var(--ink)] mb-2">{invite.email}</div>
          <p className="text-sm text-[var(--muted)] mb-3">
            {lang === "de"
              ? "Kopiere diesen Einladungs-Link und schicke ihn an den Nutzer per E-Mail oder Messenger:"
              : "Copy this invite link and share it with the user:"}
          </p>
          <div className="flex items-center gap-2 bg-[var(--bg)] border border-[var(--border)] rounded-sm px-3 py-2 font-mono text-xs break-all">
            <span className="flex-1" data-testid="invite-link-text">{invite.invite_url}</span>
            <button type="button" onClick={copy} className="shrink-0 text-[var(--muted)] hover:text-[var(--brand)]" data-testid="copy-invite-link">
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>
        </div>
        <button onClick={onDismiss} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid="dismiss-invite-banner">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function UserRow({ u, currentUser, schema, onUpdated, onDeleted, lang }) {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(u.role);
  const [permissions, setPermissions] = useState(u.permissions || {});
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const isSelf = u.id === currentUser?.id;
  const isAdminTarget = u.role === "admin";
  // Admin self-protection: an admin cannot change their own role/permissions/active.
  const isSelfAdmin = isSelf && isAdminTarget;
  const canEdit = !isSelfAdmin && (!isAdminTarget || currentUser?.role === "admin");
  const canDelete = !isSelf && !isAdminTarget;
  const canToggleActive = !isSelf && (currentUser?.role === "admin" || u.role !== "admin");

  const badge = ROLE_BADGE[u.role] || ROLE_BADGE.user;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/users/${u.id}`, { role, permissions });
      onUpdated(data);
      setEditing(false);
      toast.success(lang === "de" ? "Berechtigungen aktualisiert" : "Permissions updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    const next = !(u.active !== false);
    try {
      const { data } = await api.put(`/users/${u.id}`, { active: next });
      onUpdated(data);
      toast.success(next ? (lang === "de" ? "Aktiviert" : "Activated") : (lang === "de" ? "Deaktiviert" : "Deactivated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: lang === "de" ? "Nutzer entfernen?" : "Remove user?",
      description: `${u.name || u.email}`,
      confirmText: lang === "de" ? "Entfernen" : "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}`);
      onDeleted(u.id);
      toast.success(lang === "de" ? "Nutzer entfernt" : "User removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
      <tr className="border-b border-[var(--border)]" data-testid={`user-row-${u.email}`}>
        <td className="py-3 pr-3">
          <div className="font-medium text-[var(--ink)]">{u.name || "—"}</div>
          <div className="text-xs text-[var(--muted-2)]">{u.email}</div>
        </td>
        <td className="py-3 pr-3">
          <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-sm ${badge.cls}`}>{badge[lang]}</span>
        </td>
        <td className="py-3 pr-3">
          {u.active === false ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#FF3B30]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30]" /> {lang === "de" ? "Inaktiv" : "Inactive"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#047857]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> {lang === "de" ? "Aktiv" : "Active"}
            </span>
          )}
        </td>
        <td className="py-3 pr-3 text-sm text-[var(--muted)]">
          {u.last_login_at ? new Date(u.last_login_at).toLocaleString(lang === "de" ? "de-DE" : "en-US") : "—"}
        </td>
        <td className="py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            {canEdit && (
              <Btn variant="secondary" onClick={() => setEditing((v) => !v)} data-testid={`edit-user-${u.email}`}>
                {editing ? (lang === "de" ? "Schließen" : "Close") : (lang === "de" ? "Rechte" : "Rights")}
              </Btn>
            )}
            {canToggleActive && (
              <button type="button" onClick={toggleActive} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid={`toggle-active-${u.email}`}>
                {u.active === false ? <UserCheck size={18} /> : <UserMinus size={18} />}
              </button>
            )}
            {canDelete && (
              <button type="button" onClick={remove} className="text-[var(--muted-2)] hover:text-[#FF3B30]" data-testid={`delete-user-${u.email}`}>
                <Trash size={18} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {editing && (
        <tr className="bg-[var(--bg)]">
          <td colSpan={5} className="px-4 py-5">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={lang === "de" ? "Rolle" : "Role"}>
                  <Select
                    value={role}
                    onChange={(e) => { setRole(e.target.value); setPermissions(schema?.defaults?.[e.target.value] || {}); }}
                    disabled={isAdminTarget && currentUser?.role !== "admin"}
                    data-testid={`role-select-${u.email}`}
                  >
                    {ROLE_OPTIONS
                      .filter((r) => r.value !== "expert" || currentUser?.role === "admin")
                      .map((r) => <option key={r.value} value={r.value}>{r[lang]}</option>)}
                    {currentUser?.role === "admin" && <option value="admin">{lang === "de" ? "Admin" : "Admin"}</option>}
                  </Select>
                </Field>
              </div>
              <PermissionsEditor
                role={role}
                permissions={permissions}
                levels={schema?.levels}
                onChange={setPermissions}
                lang={lang}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Btn variant="secondary" onClick={() => setEditing(false)} data-testid={`cancel-rights-${u.email}`}>
                  {lang === "de" ? "Abbrechen" : "Cancel"}
                </Btn>
                <Btn onClick={save} disabled={busy} data-testid={`save-rights-${u.email}`}>
                  {busy ? "…" : (lang === "de" ? "Speichern" : "Save")}
                </Btn>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [latestInvite, setLatestInvite] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [u, i, s] = await Promise.all([
        api.get("/users"),
        api.get("/users/invites"),
        api.get("/permissions/schema"),
      ]);
      setUsers(u.data);
      const enriched = i.data.map((inv) => ({
        ...inv,
        invite_url: inv.invite_url?.startsWith("http")
          ? inv.invite_url
          : `${window.location.origin}${inv.invite_url || ""}`,
      }));
      setInvites(enriched);
      setSchema(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleInviteCreated = (invite) => {
    // backend returns path-only URL; frontend joins with current origin so the link
    // always works for the recipient (preview, production, custom domain).
    const fullUrl = invite.invite_url?.startsWith("http")
      ? invite.invite_url
      : `${window.location.origin}${invite.invite_url || ""}`;
    const enriched = { ...invite, invite_url: fullUrl };
    setLatestInvite(enriched);
    setInvites((prev) => {
      const filtered = prev.filter((p) => p.email !== invite.email);
      return [enriched, ...filtered];
    });
  };

  const revokeInvite = async (id) => {
    const ok = await confirm({
      title: lang === "de" ? "Einladung widerrufen?" : "Revoke invite?",
      confirmText: lang === "de" ? "Widerrufen" : "Revoke",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/users/invites/${id}`);
      setInvites((prev) => prev.filter((p) => p.id !== id));
      if (latestInvite?.id === id) setLatestInvite(null);
      toast.success(lang === "de" ? "Einladung widerrufen" : "Invite revoked");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(lang === "de" ? "Link kopiert" : "Link copied");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="08 / Team"
        title={lang === "de" ? "Nutzer" : "Users"}
        description={lang === "de"
          ? "Lade Mitarbeiter ein und steuere deren Berechtigungen pro Reiter. Einladungen sind 14 Tage gültig."
          : "Invite team members and control their permissions per tab. Invites are valid for 14 days."}
        actions={
          <Btn onClick={() => setInviteOpen(true)} data-testid="open-invite-modal">
            <Plus size={16} /> {lang === "de" ? "Einladen" : "Invite"}
          </Btn>
        }
      />

      <InviteLinkBanner invite={latestInvite} onDismiss={() => setLatestInvite(null)} lang={lang} />

      <Section title={lang === "de" ? "Aktive Mitglieder" : "Active members"}>
        {loading ? (
          <div className="text-sm text-[var(--muted)] py-6">…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-[var(--muted-2)] py-6">{lang === "de" ? "Keine Nutzer." : "No users."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[var(--muted)]">
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "Name" : "Name"}</th>
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "Rolle" : "Role"}</th>
                  <th className="text-left py-2 pr-3 label-overline">Status</th>
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "Letzter Login" : "Last login"}</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    u={u}
                    currentUser={user}
                    schema={schema}
                    onUpdated={(updated) => setUsers((prev) => prev.map((p) => p.id === updated.id ? updated : p))}
                    onDeleted={(id) => setUsers((prev) => prev.filter((p) => p.id !== id))}
                    lang={lang}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="h-6" />

      <Section title={lang === "de" ? "Offene Einladungen" : "Open invites"}>
        {invites.length === 0 ? (
          <div className="text-sm text-[var(--muted-2)] py-6">
            {lang === "de" ? "Keine offenen Einladungen." : "No open invites."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[var(--muted)]">
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "E-Mail" : "Email"}</th>
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "Rolle" : "Role"}</th>
                  <th className="text-left py-2 pr-3 label-overline">{lang === "de" ? "Gültig bis" : "Expires"}</th>
                  <th className="w-40"></th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const badge = ROLE_BADGE[inv.role] || ROLE_BADGE.user;
                  return (
                    <tr key={inv.id} className="border-b border-[var(--border)]" data-testid={`invite-row-${inv.email}`}>
                      <td className="py-3 pr-3">
                        <div className="font-medium">{inv.email}</div>
                        {inv.name && <div className="text-xs text-[var(--muted-2)]">{inv.name}</div>}
                      </td>
                      <td className="py-3 pr-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-sm ${badge.cls}`}>{badge[lang]}</span>
                      </td>
                      <td className="py-3 pr-3 text-[var(--muted)]">
                        {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString(lang === "de" ? "de-DE" : "en-US") : "—"}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Btn variant="secondary" onClick={() => copyLink(inv.invite_url)} data-testid={`copy-invite-${inv.email}`}>
                            <Copy size={14} /> {lang === "de" ? "Link" : "Link"}
                          </Btn>
                          <button type="button" onClick={() => revokeInvite(inv.id)} className="text-[var(--muted-2)] hover:text-[#FF3B30]" data-testid={`revoke-invite-${inv.email}`}>
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={handleInviteCreated}
        schema={schema}
        currentUserRole={user?.role}
        lang={lang}
      />
    </div>
  );
}
